import { storeToRefs } from 'pinia';
import { computed, watchEffect } from 'vue';
import { TOURNAMENT_CENTER_BASE_PATH, TOURNAMENT_CENTER_MATCHES_PATH } from '@/components/tournamentCenter/config';
import { useState } from '@/composable/useState';
import type { ISportListIcon } from '@/interface/ISport';
import {
    AsiaBetPageType, AsiaRoutePage, EventDateType, MarketPage, MultipleSportsZone, PlayerType, PromotionZone, PromotionZoneLayout, PromotionZoneType,
    SiteStyle, SportType,
} from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useSportLeaguePageStore } from '@/store/sportLeaguePageStore';
import { useToggleStore } from '@/store/toggleStore';

interface IIcon {
    name: string;
    size: 'sm' | 'xs' | 'md' | 'lg' | 'xl' | 'xxl' | number;
}

export interface IPromotionConfig {
    /**
     * The i18n key of title of the promotion zone
     */
    name: string;
    /**
     * The route that should be navigated to when clicking the promotion zone
     */
    route: string;
    /**
     * The base path used for registering the router structure.
     * Use this when the router's base path differs from the hotlink navigation target (e.g. B2B deep-links).
     * Falls back to `route` if not provided.
     */
    routerBasePath?: string;
    routePage: AsiaRoutePage | null;
    enabled: boolean;
    /**
     * The icon that should be displayed in the promotion zone title
     */
    icon: IIcon | null;
    hotLink: {
        /**
         * The icon that should be displayed in the hot link
         */
        icon: ISportListIcon | null;
    } | null;
    /**
     * The sport type that should be selected by default when entering the promotion zone
     * If null, the sport type will not be changed
    */
    defaultSport?: SportType | null;
    /**
     * The league names filter for the promotion zone
     */
    filteredLeagueNames: string[] | null;
    /**
     * The league ids filter for the promotion zone
     */
    filteredLeagueIds: number[] | null;
    leagueIdsBySportType?: Partial<Record<SportType, number[]>>;
    defaultBetPage: AsiaBetPageType | null;
    gtmLabel: string;
    excludeMarketPages: MarketPage[];
    routeLayout: PromotionZoneLayout;
    type: PromotionZoneType;
    supportSports?: SportType[];
    /**
     * Which multi-sport event this zone currently runs.
     * The sports list hides that event's own sport type while the zone is enabled.
     */
    multipleSportsZone?: MultipleSportsZone;
}

/*
    Only used by useAsiaSportMarketInfoStore
 */
export const _usePromotionZone = () => {
    const [promotionZone, setPromotionZone] = useState(PromotionZone.None);

    const { isEFootballEnabled } = storeToRefs(useCustomerStore());
    const {
        isShowMultipleSportsHotLink,
        isShowSportCupHotLink,
        isShowSportCup2HotLink,
        multipleSportsLeagueNames,
        multipleSportsZoneSupportSports,
        eFootballZoneLeagueIds,
        simulationSportsZoneLeagueIds,
        simulationSportsZoneSupportedSports,
        sportsCupTournamentNames,
        sportsCup2TournamentNames,
        tournamentCenterLeagueNames,
        isTournamentCenterEnabled,
        isSwitchEFootballToSimulationSportsZoneEnabled,
    } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const { sportType, leagueIds, leagueNames } = storeToRefs(useSportLeaguePageStore());

    const multipleSportsZoneEventDateOrder = [
        EventDateType.Today,
        EventDateType.Plus1,
        EventDateType.Plus2,
        EventDateType.Plus3,
        EventDateType.Plus4,
        EventDateType.Plus5,
        EventDateType.Plus6Over,
        EventDateType.All,
    ];

    const { playerType } = storeToRefs(useCustomerStore());

    const promotionZoneConfigs = computed<Record<PromotionZone, IPromotionConfig | null>>(() => ({
        [PromotionZone.None]: null,
        [PromotionZone.MultipleSports]: {
            name: `promotion_zone_${PromotionZone.MultipleSports}`,
            route: '/multiple-sports',
            routePage: AsiaRoutePage.MultipleSportsPage,
            enabled: isShowMultipleSportsHotLink.value,
            icon: { name: 'icon-sport-color-others', size: 'lg' },
            hotLink: {
                icon: { name: 'icon-sport-color-others', size: 'lg' },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: multipleSportsLeagueNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.MultipleSports,
            gtmLabel: 'Asia_Game',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Multiple,
            supportSports: multipleSportsZoneSupportSports.value,
            multipleSportsZone: MultipleSportsZone.AsianGames,
        },
        [PromotionZone.EFootball]: {
            name: `promotion_zone_${PromotionZone.EFootball}`,
            route: '/e-football',
            routePage: AsiaRoutePage.EFootballPage,
            enabled: !isSwitchEFootballToSimulationSportsZoneEnabled.value && isEFootballEnabled.value,
            icon: { name: 'icon-system-e-football', size: 'lg' },
            hotLink: {
                icon: { name: 'icon-system-e-football', size: 'lg' },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: [],
            filteredLeagueIds: eFootballZoneLeagueIds.value,
            defaultBetPage: AsiaBetPageType.EFootball,
            gtmLabel: 'eFootball',
            excludeMarketPages: [MarketPage.Outright],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.SimulationSports]: {
            name: `promotion_zone_${PromotionZone.SimulationSports}`,
            route: '/cyber-sports',
            routePage: AsiaRoutePage.SimulationSportsPage,
            enabled: isSwitchEFootballToSimulationSportsZoneEnabled.value && isEFootballEnabled.value,
            icon: { name: 'icon-sport-color-cyberSports', size: 'lg' },
            hotLink: {
                icon: { name: 'icon-sport-color-cyberSports', size: 'lg' },
            },
            filteredLeagueNames: [],
            filteredLeagueIds: Object.values(simulationSportsZoneLeagueIds.value).flat(),
            leagueIdsBySportType: simulationSportsZoneLeagueIds.value,
            defaultBetPage: AsiaBetPageType.SimulationSports,
            gtmLabel: 'CyberSports',
            excludeMarketPages: [MarketPage.Outright],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Multiple,
            supportSports: simulationSportsZoneSupportedSports.value,
        },
        [PromotionZone.SportsCup]: {
            name: `promotion_zone_${PromotionZone.SportsCup}`,
            route: '/english-premier-league',
            routePage: AsiaRoutePage.SportsCupPage,
            enabled: isShowSportCupHotLink.value,
            icon: { name: 'icon-sport-color-soccer', size: 'lg' },
            hotLink: {
                icon: {
                    name: 'icon-sport-color-soccer',
                    size: 14,
                    style: {
                        margin: '0 1px 0 1px',
                    },
                },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: sportsCupTournamentNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.SportsCup,
            gtmLabel: 'sports-cup-1',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.SportsCup2]: {
            name: `promotion_zone_${PromotionZone.SportsCup2}`,
            route: '/arctic-open',
            routePage: AsiaRoutePage.SportsCup2Page,
            enabled: isShowSportCup2HotLink.value,
            icon: { name: 'icon-sport-color-badminton', size: 'lg' },
            hotLink: {
                icon: {
                    name: 'icon-sport-color-badminton',
                    size: 14,
                    style: {
                        margin: '0 1px 0 1px',
                    },
                },
            },
            defaultSport: SportType.Badminton,
            filteredLeagueNames: sportsCup2TournamentNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.SportsCup,
            gtmLabel: 'sports-cup-2',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.SportLeague]: {
            name: `promotion_zone_${PromotionZone.SportLeague}`,
            route: '/:sportRouteName',
            routePage: AsiaRoutePage.SportLeaguePage,
            enabled: true,
            icon: { name: 'icon-system-fire', size: 'lg' },
            hotLink: null,
            defaultSport: sportType.value,
            filteredLeagueNames: leagueNames.value,
            filteredLeagueIds: leagueIds.value,
            defaultBetPage: AsiaBetPageType.SportLeague,
            gtmLabel: 'SportLeague',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.TournamentCenter]: {
            name: `promotion_zone_${PromotionZone.TournamentCenter}`,
            route: playerType.value === PlayerType.B2B ? TOURNAMENT_CENTER_MATCHES_PATH : TOURNAMENT_CENTER_BASE_PATH,
            routerBasePath: TOURNAMENT_CENTER_BASE_PATH,
            routePage: null,
            enabled: isTournamentCenterEnabled.value,
            icon: null,
            hotLink: {
                icon: {
                    name: 'icon-sport-color-worldCupColor',
                    size: 18,
                    style: {
                        margin: '0 -2px 0 0',
                    },
                },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: tournamentCenterLeagueNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.TournamentCenterMatches,
            gtmLabel: 'tournament-center',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.TournamentCenter,
            type: PromotionZoneType.Single,
        },
    }));

    /**
     * Known Bug: If two PromotionZones share identical `defaultSport`, `filteredLeagueNames`,
     * and `filteredLeagueIds`, the zone-switching logic will malfunction and the wrong zone
     * may become active.
     *
     * This should never happen under normal business configurations, but if it does
     * (e.g. misconfigured toggles), this computed will surface the conflicting pair
     * and a console.warn will be emitted to alert developers.
     */
    const conflictingPromotionZones = computed(() => {
        const sortedStringify = (arr: unknown[] | null) => JSON.stringify([...(arr ?? [])].sort((a, b) => String(a).localeCompare(String(b))));

        const configs = Object.entries(promotionZoneConfigs.value)
            .filter((entry): entry is [string, IPromotionConfig] => !!entry[1]?.enabled);

        for (let i = 0; i < configs.length; i++) {
            for (let j = i + 1; j < configs.length; j++) {
                const [keyA, a] = configs[i];
                const [keyB, b] = configs[j];

                const sameSport = a.defaultSport === b.defaultSport;
                const sameLeagueNames = sortedStringify(a.filteredLeagueNames)
                    === sortedStringify(b.filteredLeagueNames);
                const sameLeagueIds = sortedStringify(a.filteredLeagueIds)
                    === sortedStringify(b.filteredLeagueIds);

                if (sameSport && sameLeagueNames && sameLeagueIds) {
                    return { keyA, keyB, nameA: a.name, nameB: b.name };
                }
            }
        }
        return null;
    });

    watchEffect(() => {
        const conflict = conflictingPromotionZones.value;
        if (conflict && !import.meta.env.PROD) {
            // eslint-disable-next-line no-alert
            alert(
                '[usePromotionZone] Known Bug: Two PromotionZones share the same defaultSport, filteredLeagueNames, and filteredLeagueIds.\n'
                + 'This will cause incorrect zone-switching behavior — the system cannot distinguish between them.\n'
                + 'Business logic should prevent this from happening. Please check your toggle/config settings.\n'
                + `Zone A: ${conflict.keyA} (${conflict.nameA})\n`
                + `Zone B: ${conflict.keyB} (${conflict.nameB})`,
            );
        }
    });

    const getPromotionZoneConfig = (promotionZone: PromotionZone) => promotionZoneConfigs.value[promotionZone] ?? null;
    const currentPromotionZoneConfig = computed(() => getPromotionZoneConfig(promotionZone.value));

    return {
        promotionZone,
        setPromotionZone,
        getPromotionZoneConfig,
        currentPromotionZoneConfig,
        multipleSportsZoneEventDateOrder,
    };
};
